import { FC, useState, useContext } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Loading } from '@appkit4/react-components';
import { SessionContext } from '@contexts/SessionProvider';
import { linkGithubRepo, unlinkGithubRepo, pushToGithub } from '@config/getGithubApis';
import { UserContext } from '@contexts/UserProvider';
import styles from './GithubPublishModal.module.scss';

interface GithubPublishModalProps {
    visible: boolean;
    onCancel: () => void;
    onLeaseFailure: () => void;
}

const GithubPublishModal: FC<GithubPublishModalProps> = ({ visible, onCancel, onLeaseFailure }) => {
    const [repoUrl, setRepoUrl] = useState('');
    const [branch, setBranch] = useState('design');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const {
        sessionId,
        githubLinked,
        githubRepoUrl,
        githubBranch,
        githubLastPushedAt,
        githubLastPushStatus,
        setGithubLinked,
        setGithubRepoUrl,
        setGithubBranch,
        setGithubLastPushedAt,
        setGithubLastPushedSha,
        setGithubLastPushStatus,
        refreshGithubSessionStatus,
    } = useContext(SessionContext);

    const { setGithubLastAuthFailureAt } = useContext(UserContext);

    const handleLinkAndPublish = async () => {
        if (!repoUrl.trim()) return;

        setIsLoading(true);
        setErrorMessage('');

        try {
            const linkResponse = await linkGithubRepo(sessionId, repoUrl.trim(), branch || 'design');
            if (!linkResponse || linkResponse.code !== 1) {
                const errCode = linkResponse?.resultObject?.errorCode || '';
                const desc = linkResponse?.description || 'Failed to link repository.';
                if (errCode === 'NOT_CONNECTED') {
                    setErrorMessage('GitHub is not connected. Please connect your PAT first.');
                } else if (errCode === 'REPO_NOT_FOUND') {
                    setErrorMessage('Repository not found. Check the URL and your token permissions.');
                } else if (errCode === 'AUTH_FAILED') {
                    setErrorMessage('Authentication failed. Your token may be expired.');
                } else {
                    setErrorMessage(desc);
                }
                return;
            }

            const status = linkResponse.resultObject;
            setGithubLinked(status.linked ?? true);
            setGithubRepoUrl(status.repoUrl ?? repoUrl.trim());
            setGithubBranch(status.branch ?? branch);
            setGithubLastPushedAt(status.lastPushedAt ?? null);
            setGithubLastPushedSha(status.lastPushedSha ?? null);
            setGithubLastPushStatus(status.lastPushStatus ?? null);

            setRepoUrl('');
            setBranch('design');
            onCancel();
        } catch (error: any) {
            const errCode = error?.response?.data?.errorCode || error?.response?.data?.resultObject?.errorCode || '';
            if (errCode === 'LEASE_FAILED') {
                onCancel();
                onLeaseFailure();
            } else if (errCode === 'AUTH_FAILED') {
                setGithubLastAuthFailureAt(new Date().toISOString());
                onCancel();
            } else {
                const msg = error?.response?.data?.description || 'Failed to link and publish.';
                setErrorMessage(msg);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePush = async () => {
        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await pushToGithub(sessionId);
            if (!response || response.code !== 1) {
                const errCode = response?.resultObject?.errorCode || '';
                const desc = response?.description || 'Push failed.';
                if (errCode === 'LEASE_FAILED') {
                    onCancel();
                    onLeaseFailure();
                    return;
                } else if (errCode === 'AUTH_FAILED') {
                    setGithubLastAuthFailureAt(new Date().toISOString());
                    onCancel();
                    return;
                } else {
                    setErrorMessage(desc);
                }
                return;
            }

            const result = response.resultObject;
            setGithubLastPushedAt(result.pushedAt ?? new Date().toISOString());
            setGithubLastPushedSha(result.headSha ?? null);
            setGithubLastPushStatus('ok');
            await refreshGithubSessionStatus(sessionId);
            onCancel();
        } catch (error: any) {
            const errCode = error?.response?.data?.errorCode || error?.response?.data?.resultObject?.errorCode || '';
            if (errCode === 'LEASE_FAILED') {
                onCancel();
                onLeaseFailure();
            } else if (errCode === 'AUTH_FAILED') {
                setGithubLastAuthFailureAt(new Date().toISOString());
                onCancel();
            } else {
                const msg = error?.response?.data?.description || 'Push failed.';
                setErrorMessage(msg);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleUnlink = async () => {
        if (!window.confirm('Unlink this session from GitHub? You can re-link at any time.')) return;

        setIsLoading(true);
        try {
            await unlinkGithubRepo(sessionId);
            setGithubLinked(false);
            setGithubRepoUrl('');
            setGithubBranch('design');
            setGithubLastPushedAt(null);
            setGithubLastPushedSha(null);
            setGithubLastPushStatus(null);
            onCancel();
        } catch (error) {
            console.error('Error unlinking GitHub repo', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (!isLoading) {
            setRepoUrl('');
            setBranch('design');
            setErrorMessage('');
            onCancel();
        }
    };

    const renderLinkedState = () => (
        <div className={styles['linked-info']}>
            <div className={styles['linked-row']}>
                <span className={styles['linked-label']}>Repository</span>
                <span className={styles['linked-value']}>{githubRepoUrl}</span>
            </div>
            <div className={styles['linked-row']}>
                <span className={styles['linked-label']}>Branch</span>
                <span className={styles['linked-value']}>{githubBranch}</span>
            </div>
            {githubLastPushedAt && (
                <div className={styles['linked-row']}>
                    <span className={styles['linked-label']}>Last pushed</span>
                    <span className={styles['linked-value']}>
                        {new Date(githubLastPushedAt.replace(' ', 'T') + 'Z').toLocaleString()}
                    </span>
                </div>
            )}
            {githubLastPushStatus && (
                <div className={styles['linked-row']}>
                    <span className={styles['linked-label']}>Last push status</span>
                    <span
                        className={
                            githubLastPushStatus === 'ok' ? styles['push-status-ok'] : styles['push-status-failed']
                        }>
                        {githubLastPushStatus}
                    </span>
                </div>
            )}
            {errorMessage && (
                <p className={styles['error-message']}>
                    <span className="Appkit4-icon icon-alert-outline" aria-hidden="true" />
                    {errorMessage}
                </p>
            )}
            {isLoading && (
                <div className={styles['loading-row']}>
                    <Loading loadingType="circular" indeterminate compact />
                    <span>Pushing...</span>
                </div>
            )}
        </div>
    );

    const renderNotLinkedState = () => (
        <div className={styles['publish-modal-body']}>
            <Input
                title="Repository URL"
                value={repoUrl}
                onChange={(v: string) => {
                    setRepoUrl(v);
                    setErrorMessage('');
                }}
                placeholder="https://github.com/owner/repo"
                hideTitleOnInput={false}
            />
            {/*<Input
                title="Branch (optional)"
                value={branch}
                onChange={(v: string) => setBranch(v)}
                placeholder="design"
                hideTitleOnInput={false}
            />*/}
            <p className={styles['helper-text']}>
                {"We'll push your project code to the "}
                <code>{branch || 'design'}</code>
                {' branch'}
            </p>
            {errorMessage && (
                <p className={styles['error-message']}>
                    <span className="Appkit4-icon icon-alert-outline" aria-hidden="true" />
                    {errorMessage}
                </p>
            )}
        </div>
    );

    const renderFooter = () => {
        if (githubLinked) {
            return (
                <>
                    <Button kind="text" onClick={handleUnlink} disabled={isLoading}>
                        Unlink
                    </Button>
                    <Button kind="secondary" onClick={handleCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button kind="primary" onClick={handlePush} disabled={isLoading}>
                        Push now
                    </Button>
                </>
            );
        }
        return (
            <>
                <Button kind="secondary" onClick={handleCancel} disabled={isLoading}>
                    Cancel
                </Button>
                <Button kind="primary" onClick={handleLinkAndPublish} disabled={!repoUrl.trim() || isLoading}>
                    {isLoading ? 'Linking...' : 'Link & Publish'}
                </Button>
            </>
        );
    };

    return (
        <Modal
            visible={visible}
            title="Publish to GitHub"
            ariaLabel="Publish to GitHub"
            onCancel={handleCancel}
            modalStyle={{ width: '520px' }}
            footer={renderFooter()}>
            {githubLinked === true ? renderLinkedState() : renderNotLinkedState()}
        </Modal>
    );
};

export default GithubPublishModal;
